import { useEffect, useRef, useState } from 'react';

import { Box, Button, ButtonGroup, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconArrowLeftOutline, IconArrowRightOutline } from '@zillow/constellation-icons';

import { EmailVerificationType, PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-beta-client';
import {
  useRentalManagerUserClaimWebApiHandler_getExclusivePropertiesForAllUnverified,
  useRentalManagerUserClaimWebApiHandler_list,
  useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { combineContactMethods, getVerifiedContacts } from '../../utils/listingClaimUtils';
import numberUtils from '../../../srcLegacy/shared/utils/numberUtils';

import ListingRemovalModal from '../modals/ListingRemovalModal';
import ManageClaimContactMethodsModal from '../modals/ManageClaimContactMethodsModal';
import { VerificationModal } from '../modals/VerificationModal';
import ViewClaimListingsModal from '../modals/ViewClaimListingsModal';
import WizardBodyTemplate from '../WizardBodyTemplate';
import ContactMethodDetail from './ContactMethodDetail';

import { VERIFICATION_STEPS } from '../modals/VerificationModal/constants';
import { ROUTES } from '../../constants/urls';
import { STEP } from './constants';

import type { UserClaimGetExclusivePropertiesForAllUnverifiedListingDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { WizardBodyTemplateProps } from '../WizardStepController';

const { formatPhoneNumberInput } = numberUtils;
const verificationConfig = [
  VERIFICATION_STEPS.GET_VERIFICATION_CODE,
  VERIFICATION_STEPS.VERIFICATION_CODE_SENT,
  VERIFICATION_STEPS.VERIFICATION_SUCCESS,
];

const WizVerifyContactList = ({
  onGoToNextStep,
  onGoToPrevStep,
  onGoToStep,
  progressStepper,
}: WizardBodyTemplateProps) => {
  const { enqueueToast } = useToast();

  const [listings, setListings] = useState<Array<UserClaimGetExclusivePropertiesForAllUnverifiedListingDto>>([]);
  const [unverifiedContactMethods, setUnverifiedContactMethods] = useState<{
    emails: Array<string | { value: string }>;
    phones: Array<string | { value: string }>;
  }>({ emails: [], phones: [] });
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [phoneToVerify, setPhoneToVerify] = useState('');
  const [showManageContactMethodsModal, setShowManageContactMethodsModal] = useState(false);
  const [showListingRemovalModal, setShowListingRemovalModal] = useState(false);
  const [showPhoneVerificationWizard, setShowPhoneVerificationWizard] = useState(false);
  const [showViewListingsModal, setShowViewListingsModal] = useState(false);
  const [pendingEmails, setPendingEmails] = useState<Array<string>>([]);
  const initialRender = useRef(true);

  const { data: listData, refetch: refetchList, isError: hasError } = useRentalManagerUserClaimWebApiHandler_list();
  const { refetch: refetchExclusive } = useRentalManagerUserClaimWebApiHandler_getExclusivePropertiesForAllUnverified(
    {},
    { enabled: false },
  );
  const { mutateAsync: sendSecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken();

  const contactMethods = listData ? { emails: listData.emails, phones: listData.phones } : { emails: [], phones: [] };
  const contactMethodsCount = contactMethods.emails.length + contactMethods.phones.length;
  const contactMethodsList = combineContactMethods(contactMethods);

  useEffect(() => {
    if (listData) {
      setIsTransitioning(false);
    }
  }, [listData]);

  useEffect(() => {
    if (contactMethodsCount === 0 && !showManageContactMethodsModal && !isTransitioning) {
      onGoToStep(STEP.FIND_LISTING_RESULTS);
    }
  }, [contactMethodsCount, showManageContactMethodsModal, isTransitioning, onGoToStep]);

  const handlePhoneVerificationComplete = (verifiedPhoneNumber: string) => {
    const indexOfVerifiedPhone = contactMethods.phones.findIndex(
      (contactObj: { value: string }) => contactObj.value === verifiedPhoneNumber,
    );
    if (indexOfVerifiedPhone >= 0) {
      refetchList();
      return Promise.resolve();
    }
    return Promise.reject(new Error('There was an error. Please try again.'));
  };

  const handleOnVerifyPhoneClick = (phoneNumber: string) => {
    setShowPhoneVerificationWizard(true);
    setPhoneToVerify(phoneNumber);
  };

  const handleOnVerifyEmailClick = (email: string) => {
    const successRedirectPath = `${ROUTES.LISTING_CLAIMING}?step=${STEP.VERIFY_CONTACT_LIST}`;
    sendSecurityToken({
      getParams: {
        email,
        emailVerificationType: EmailVerificationType.LISTING_CLAIMING_PATH,
        successRedirectPath,
      },
    })
      .then((response) => {
        setPendingEmails((prev) => [...prev, email]);
        enqueueToast(<Toast tone="success">Verification email sent</Toast>);
        return response;
      })
      .catch(() =>
        enqueueToast(
          <Toast tone="critical">There was an error sending the verification email. Please try again.</Toast>,
        ),
      );
  };

  const handleCloseManageContactModal = () => {
    setShowManageContactMethodsModal(false);
    setIsTransitioning(true);
    refetchList().finally(() => setIsTransitioning(false));
  };

  const handleNextClick = async () => {
    const { data } = await refetchExclusive();
    const unverifiedListings = data?.listings ?? [];
    const newContactMethods = data?.contactMethods
      ? { emails: data.contactMethods.emails, phones: data.contactMethods.phones }
      : { emails: [], phones: [] };
    if (unverifiedListings.length > 0) {
      setListings(unverifiedListings);
      setUnverifiedContactMethods(newContactMethods);
      setShowListingRemovalModal(true);
    } else {
      onGoToNextStep?.();
    }
  };

  const getEmailVerificationText = (email: string) => {
    const emailsAction = pendingEmails.includes(email) ? 'Resend' : 'Send';
    return `${emailsAction} verification email`;
  };

  const renderSubtitle = () => {
    const subtitle = 'To ensure we find all your listings to transfer, ';
    let contactVerificationSubtitle: string;
    if (initialRender.current) {
      const email = contactMethods.emails[0]?.value;
      const phone = contactMethods.phones[0]?.value;
      const contactToRender = email || formatPhoneNumberInput(phone ?? '');
      contactVerificationSubtitle =
        contactMethodsCount > 1 ? `verify these ${contactMethodsCount} contact methods.` : `verify ${contactToRender}`;
    } else {
      const isContactsVerified = contactMethodsList.every((c) => c.isVerified);
      contactVerificationSubtitle = isContactsVerified
        ? 'you’ve verified all the contacts.'
        : 'make sure you verify all the contacts.';
    }
    return (
      <>
        <Paragraph>{subtitle}</Paragraph>
        <Paragraph className="fs-mask">{contactVerificationSubtitle}</Paragraph>
      </>
    );
  };

  return (
    <WizardBodyTemplate
      title="Contact verification"
      subtitle={renderSubtitle()}
      isTransitioning={isTransitioning}
      hasError={hasError}
      progressStepper={progressStepper}
      footer={
        <ButtonGroup aria-label="Actions" css={{ justifyContent: 'center' }}>
          <Button
            tone="brand"
            emphasis="outlined"
            onClick={onGoToPrevStep ?? undefined}
            disabled={!onGoToPrevStep}
            icon={<IconArrowLeftOutline />}
          >
            Back
          </Button>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleNextClick}
            disabled={!getVerifiedContacts(contactMethodsList)}
            icon={<IconArrowRightOutline />}
            iconPosition="end"
          >
            Next
          </Button>
        </ButtonGroup>
      }
    >
      <Box css={{ marginBottom: '400' }}>
        <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <TextButton onClick={() => setShowManageContactMethodsModal(true)}>Manage contact methods</TextButton>
          <Box
            css={{ margin: '0 {spacing.300}', width: '25', height: '300', backgroundColor: 'bg.action.hero.default' }}
          />
          <TextButton onClick={() => setShowViewListingsModal(true)}>View listings</TextButton>
        </Box>
      </Box>

      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
        <Box css={{ gridColumnStart: { base: '1', lg: '2' }, gridColumnEnd: { base: 'span 12', lg: 'span 10' } }}>
          <Box css={{ maxHeight: '600px', overflowY: 'auto' }}>
            {contactMethodsList.map((contactMethod) => (
              <ContactMethodDetail
                key={contactMethod.value}
                data={{
                  value: contactMethod.value ?? '',
                  isVerified: contactMethod.isVerified ?? false,
                  canBeRemoved: (contactMethod as { canBeRemoved?: boolean }).canBeRemoved ?? true,
                }}
                emailVerificationText={getEmailVerificationText(contactMethod.value ?? '')}
                inManageContactModal={false}
                onVerifyPhoneClick={handleOnVerifyPhoneClick}
                onVerifyEmailClick={handleOnVerifyEmailClick}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {showManageContactMethodsModal && <ManageClaimContactMethodsModal onClose={handleCloseManageContactModal} />}

      {showListingRemovalModal && (
        <ListingRemovalModal
          onClose={() => setShowListingRemovalModal(false)}
          onSubmitSuccess={() => onGoToNextStep?.()}
          listings={listings}
          contactMethods={unverifiedContactMethods}
        />
      )}

      {showPhoneVerificationWizard && (
        <VerificationModal
          headerText="Verify phone number"
          isContentEditable={false}
          showVerificationModal={setShowPhoneVerificationWizard}
          stepsConfig={verificationConfig}
          initialContentToVerify={phoneToVerify}
          verificationSuccessfulCTAText="Return to contact verification"
          onVerificationConfirmation={handlePhoneVerificationComplete}
          verificationSource={PhoneVerificationSource.LISTING_CLAIMING_PATH}
        />
      )}

      {showViewListingsModal && (
        <ViewClaimListingsModal
          isOpen={showViewListingsModal}
          onClose={() => setShowViewListingsModal(false)}
          includeUnverified={true}
        />
      )}
    </WizardBodyTemplate>
  );
};

export default WizVerifyContactList;
